import {
  OneColumnGroupDto,
  TwoColumnGroupDto,
  WidgetDto,
} from '@asset-metrix/portal-gateway-client-js';

import { DraftDashboardTile } from './utils/dashboard.types';
import { isHydratedAvailable, isUnavailableTile } from './dashboardTileGuards';

type ColumnGroupDto = OneColumnGroupDto | TwoColumnGroupDto;

function createSingleColumnGroup(widget: WidgetDto): OneColumnGroupDto {
  return {
    type: 'singleColumnGroup',
    contents: [widget],
  };
}

function createTwoColumnGroup(
  contents: TwoColumnGroupDto['contents']
): TwoColumnGroupDto {
  return {
    type: 'twoColumnGroup',
    contents,
  };
}

/**
 * Packs dashboard tiles into column groups by widthFactor.
 * Values ≤ 0.5 are half-width; values > 0.5 are full-width.
 * Exact widthFactor is preserved on the tile; only layout pairing uses the threshold.
 */
export function regroupDashboardTilesToContents(
  tiles: readonly DraftDashboardTile[]
): Array<{
  group: ColumnGroupDto;
  placements: DraftDashboardTile[];
}> {
  type Row = {
    group: ColumnGroupDto;
    placements: DraftDashboardTile[];
  };

  const rows: Row[] = [];
  let freeHalfSlot = false;

  for (const tile of tiles) {
    const widget = tileToRenderableWidget(tile);
    if (tile.widthFactor <= 0.5) {
      const wrapped = createSingleColumnGroup(widget);
      if (freeHalfSlot) {
        const last = rows.at(-1);
        if (last?.group.type === 'twoColumnGroup') {
          last.group.contents.push(wrapped);
          last.placements.push(tile);
        }
        freeHalfSlot = false;
      } else {
        rows.push({
          group: createTwoColumnGroup([wrapped]),
          placements: [tile],
        });
        freeHalfSlot = true;
      }
      continue;
    }

    rows.push({
      group: createSingleColumnGroup(widget),
      placements: [tile],
    });
    freeHalfSlot = false;
  }

  return rows;
}

function tileToRenderableWidget(tile: DraftDashboardTile): WidgetDto {
  if (isHydratedAvailable(tile)) {
    return tile.content;
  }

  // Unavailable or Add-placeholder: synthetic widget shell for layout pairing only.
  return {
    id: tile.contentId,
    type: 'widget',
    title: '',
    entity: {
      type: 'table',
      config: {
        id: tile.contentId,
        enableAIMetadataExtraction: false,
        enableRowSelection: false,
        enableDocumentZipUpload: false,
        enableDocviewSidebar: false,
        enablePatternBasedRenaming: false,
        hidePagination: true,
        title: '',
        cacheKey: { dataSourceType: 'datatable' },
        customizable: false,
        showFullTextSearchResults: false,
        sortColumns: [],
        columns: [],
      },
    },
  };
}

export function collectPlacementMeta(tiles: readonly DraftDashboardTile[]): Map<
  string,
  {
    instanceId: string;
    contentId: string;
    widthFactor: number;
    kind: 'available' | 'unavailable' | 'placeholder';
  }
> {
  const map = new Map<
    string,
    {
      instanceId: string;
      contentId: string;
      widthFactor: number;
      kind: 'available' | 'unavailable' | 'placeholder';
    }
  >();

  for (const tile of tiles) {
    const kind = isHydratedAvailable(tile)
      ? 'available'
      : isUnavailableTile(tile)
        ? 'unavailable'
        : 'placeholder';
    map.set(tile.instanceId, {
      instanceId: tile.instanceId,
      contentId: tile.contentId,
      widthFactor: tile.widthFactor,
      kind,
    });
  }

  return map;
}
